export type IngredientFunctionCategory =
  | 'Preservatives'
  | 'Acidity regulators'
  | 'Thickeners & stabilizers'
  | 'Emulsifiers'
  | 'Sweeteners'
  | 'Colors'
  | 'Flavoring ingredients'
  | 'Antioxidants'
  | 'Other common ingredients'

export type CommonIngredient = {
  number: number
  name: string
  alsoCalled?: string
  category: IngredientFunctionCategory
  emoji: string
  whatIsIt: string
  whyUsed: string[]
  foundIn: string[]
  toKnow?: string
  toKnowLabel?: string
  mainFunction: string
}

export const ingredientCategories: {
  category: IngredientFunctionCategory
  does: string
  emoji: string
}[] = [
  { category: 'Preservatives', does: 'Help slow spoilage', emoji: '🧊' },
  { category: 'Acidity regulators', does: 'Control acidity and pH', emoji: '🍋' },
  { category: 'Thickeners & stabilizers', does: 'Improve texture and consistency', emoji: '🥄' },
  { category: 'Emulsifiers', does: 'Help ingredients stay mixed', emoji: '🧴' },
  { category: 'Sweeteners', does: 'Add sweetness', emoji: '🍬' },
  { category: 'Colors', does: 'Add or restore color', emoji: '🎨' },
  { category: 'Flavoring ingredients', does: 'Add or enhance flavor', emoji: '🌸' },
  { category: 'Antioxidants', does: 'Help protect food from oxidation', emoji: '🛡️' },
  { category: 'Other common ingredients', does: 'Serve various food-processing functions', emoji: '⚙️' },
]

export const commonIngredients: CommonIngredient[] = [
  {
    number: 1,
    name: 'Maltodextrin',
    category: 'Other common ingredients',
    emoji: '🌽',
    whatIsIt:
      'Maltodextrin is a carbohydrate produced by breaking down starch, commonly from corn, rice, potato, or other starch sources.',
    whyUsed: ['Adds bulk', 'Improves texture', 'Helps carry flavors', 'Provides carbohydrates', 'Helps create a desired consistency'],
    foundIn: ['Snack foods', 'Drink mixes', 'Sauces', 'Processed foods', 'Sports nutrition products'],
    toKnow:
      'Maltodextrin is a highly processed carbohydrate ingredient. People monitoring carbohydrate intake may want to pay attention to how much of it is present in the overall product.',
    mainFunction: 'Bulking / texture',
  },
  {
    number: 2,
    name: 'Citric Acid',
    category: 'Acidity regulators',
    emoji: '🍋',
    whatIsIt: 'Citric acid is an organic acid naturally found in citrus fruits and widely used as a food ingredient.',
    whyUsed: ['Adds tartness', 'Controls acidity', 'Helps preserve food quality', 'Supports flavor stability'],
    foundIn: ['Beverages', 'Candy', 'Sauces', 'Canned foods', 'Jams', 'Snacks'],
    toKnow:
      'Citric acid is a common food ingredient and acidity regulator. Its presence doesn’t mean that a product contains a large amount of citrus fruit.',
    mainFunction: 'Acidity regulator',
  },
  {
    number: 3,
    name: 'Xanthan Gum',
    category: 'Thickeners & stabilizers',
    emoji: '🥗',
    whatIsIt:
      'Xanthan gum is a polysaccharide produced through fermentation and used as a thickening and stabilizing agent.',
    whyUsed: ['Thickens foods', 'Stabilizes mixtures', 'Helps prevent separation', 'Improves texture'],
    foundIn: ['Salad dressings', 'Sauces', 'Gluten-free baked goods', 'Dairy alternatives', 'Packaged foods'],
    toKnow: 'It is particularly useful in gluten-free foods because it can help provide structure and texture.',
    mainFunction: 'Thickener / stabilizer',
  },
  {
    number: 4,
    name: 'Lecithin',
    category: 'Emulsifiers',
    emoji: '🍫',
    whatIsIt: 'Lecithin is a mixture of phospholipids commonly derived from soy, sunflower, or eggs.',
    whyUsed: ['Helps ingredients mix', 'Prevents separation', 'Improves texture'],
    foundIn: ['Chocolate', 'Baked goods', 'Margarine', 'Protein products', 'Processed foods'],
    toKnow:
      'The source matters for people with allergies or specific dietary preferences. Soy-derived lecithin may be relevant to someone with a soy allergy.',
    mainFunction: 'Emulsifier',
  },
  {
    number: 5,
    name: 'Sodium Benzoate',
    category: 'Preservatives',
    emoji: '🥤',
    whatIsIt: 'Sodium benzoate is the sodium salt of benzoic acid and is used as a preservative.',
    whyUsed: ['Helps inhibit microbial growth', 'Extends shelf life', 'Protects certain acidic foods and beverages'],
    foundIn: ['Soft drinks', 'Fruit beverages', 'Sauces', 'Pickled products', 'Condiments'],
    toKnow: 'Its use is regulated, and permitted amounts depend on the food and regulatory requirements.',
    mainFunction: 'Preservative',
  },
  {
    number: 6,
    name: 'Potassium Sorbate',
    category: 'Preservatives',
    emoji: '🧀',
    whatIsIt: 'Potassium sorbate is a preservative derived from sorbic acid.',
    whyUsed: ['It helps inhibit the growth of certain molds, yeasts, and microorganisms.'],
    foundIn: ['Cheese', 'Baked goods', 'Sauces', 'Dried fruit', 'Beverages'],
    toKnow: 'It is widely used because it can help maintain food quality and extend shelf life.',
    mainFunction: 'Preservative',
  },
  {
    number: 7,
    name: 'Carrageenan',
    category: 'Thickeners & stabilizers',
    emoji: '🌊',
    whatIsIt: 'Carrageenan is a carbohydrate extracted from certain red seaweeds.',
    whyUsed: ['Thickens foods', 'Stabilizes mixtures', 'Improves texture'],
    foundIn: ['Plant-based milk', 'Dairy products', 'Desserts', 'Processed foods'],
    toKnow:
      'Carrageenan is the subject of ongoing scientific and consumer discussion. It is important to distinguish food-grade carrageenan from laboratory research involving different substances or experimental conditions.',
    mainFunction: 'Thickener / stabilizer',
  },
  {
    number: 8,
    name: 'Guar Gum',
    category: 'Thickeners & stabilizers',
    emoji: '🫘',
    whatIsIt: 'Guar gum is a soluble fiber derived from guar beans.',
    whyUsed: ['Thickening', 'Stabilizing', 'Improving texture'],
    foundIn: ['Ice cream', 'Sauces', 'Dairy alternatives', 'Gluten-free foods'],
    toKnow: 'It can absorb water and increase viscosity, which is why only relatively small quantities may be needed.',
    mainFunction: 'Thickener',
  },
  {
    number: 9,
    name: 'Pectin',
    category: 'Thickeners & stabilizers',
    emoji: '🍏',
    whatIsIt: 'Pectin is a naturally occurring polysaccharide found in fruits.',
    whyUsed: ['Thickens foods', 'Helps form gels', 'Improves texture'],
    foundIn: ['Jams', 'Jellies', 'Fruit preparations', 'Yogurt', 'Some beverages'],
    toKnow: 'Pectin is also a source of soluble dietary fiber.',
    mainFunction: 'Gelling / thickener',
  },
  {
    number: 10,
    name: 'Agar-Agar',
    category: 'Thickeners & stabilizers',
    emoji: '🍮',
    whatIsIt: 'Agar-agar is a substance derived from certain types of seaweed.',
    whyUsed: ['Thickening', 'Gelling', 'Stabilizing'],
    foundIn: ['Desserts', 'Plant-based foods', 'Jellies', 'Confectionery'],
    toKnow: 'Agar is commonly used as a plant-based alternative to gelatin.',
    mainFunction: 'Gelling agent',
  },
  {
    number: 11,
    name: 'Ascorbic Acid',
    category: 'Antioxidants',
    emoji: '🍊',
    whatIsIt: 'Ascorbic acid is vitamin C.',
    whyUsed: ['Antioxidant', 'Helps maintain food quality', 'Can help prevent oxidation'],
    foundIn: ['Bread', 'Beverages', 'Cereals', 'Fruit products'],
    toKnow:
      'When listed as ascorbic acid, it doesn’t necessarily mean the product is being marketed primarily as a vitamin C supplement. It may be present because of its technological function.',
    mainFunction: 'Antioxidant',
  },
  {
    number: 12,
    name: 'Tocopherols',
    category: 'Antioxidants',
    emoji: '🫗',
    whatIsIt: 'Tocopherols are forms of vitamin E.',
    whyUsed: ['They can function as antioxidants and help protect fats and oils from oxidation.'],
    foundIn: ['Cereals', 'Snack foods', 'Oils', 'Processed foods'],
    toKnow: 'Vitamin E compounds can serve an important food-preservation function.',
    mainFunction: 'Antioxidant',
  },
  {
    number: 13,
    name: 'Rosemary Extract',
    category: 'Antioxidants',
    emoji: '🌿',
    whatIsIt: 'Rosemary extract is derived from rosemary and may be used as an antioxidant.',
    whyUsed: ['Helps protect oils and fats from oxidation', 'Helps maintain product quality'],
    foundIn: ['Snack foods', 'Meat products', 'Oils', 'Processed foods'],
    mainFunction: 'Antioxidant',
  },
  {
    number: 14,
    name: 'Sodium Nitrite',
    category: 'Preservatives',
    emoji: '🥓',
    whatIsIt: 'Sodium nitrite is a curing agent used in certain processed meat products.',
    whyUsed: ['Helps control microbial growth', 'Contributes to characteristic cured-meat color', 'Helps develop flavor'],
    foundIn: ['Bacon', 'Ham', 'Sausages', 'Deli meats'],
    toKnow:
      'Nitrites have an important technological and food-safety role, but processed meat consumption is also an area where overall dietary patterns matter.',
    mainFunction: 'Curing / preservative',
  },
  {
    number: 15,
    name: 'Calcium Propionate',
    category: 'Preservatives',
    emoji: '🍞',
    whatIsIt: 'Calcium propionate is a preservative commonly used in baked goods.',
    whyUsed: ['It helps inhibit mold growth.'],
    foundIn: ['Bread', 'Rolls', 'Baked goods'],
    toKnow: 'It can help extend the shelf life of bread and reduce food waste.',
    mainFunction: 'Preservative',
  },
  {
    number: 16,
    name: 'Sodium Bicarbonate',
    alsoCalled: 'Common name: Baking soda',
    category: 'Other common ingredients',
    emoji: '🧁',
    whatIsIt: 'Sodium bicarbonate is the chemical name for baking soda.',
    whyUsed: ['Leavening', 'pH control', 'Texture'],
    foundIn: ['Cookies', 'Cakes', 'Bread', 'Crackers'],
    toKnow: 'This is a familiar household ingredient that can appear on food labels under its chemical name.',
    mainFunction: 'Leavening',
  },
  {
    number: 17,
    name: 'Monocalcium Phosphate',
    category: 'Other common ingredients',
    emoji: '🥞',
    whatIsIt: 'Monocalcium phosphate is a leavening acid and acidity regulator.',
    whyUsed: ['Leavening', 'Acidity regulation'],
    foundIn: ['Baking mixes', 'Biscuits', 'Pancakes', 'Baked goods'],
    toKnow: 'It reacts with other ingredients to help produce carbon dioxide and contribute to rising.',
    mainFunction: 'Leavening',
  },
  {
    number: 18,
    name: 'Potassium Chloride',
    category: 'Other common ingredients',
    emoji: '🧂',
    whatIsIt: 'A potassium salt.',
    whyUsed: ['It can be used as a mineral source or as a sodium-reduction strategy in some foods.'],
    foundIn: ['Reduced-sodium products', 'Processed foods', 'Some beverages'],
    toKnow: 'It can taste bitter at higher concentrations, so formulations are carefully balanced.',
    mainFunction: 'Mineral / sodium reduction',
  },
  {
    number: 19,
    name: 'Calcium Carbonate',
    category: 'Other common ingredients',
    emoji: '🦴',
    whatIsIt: 'A calcium-containing mineral compound.',
    whyUsed: ['Calcium fortification', 'Acidity control', 'Other processing functions'],
    foundIn: ['Fortified foods', 'Cereals', 'Some beverages'],
    mainFunction: 'Fortification',
  },
  {
    number: 20,
    name: 'Ferric Orthophosphate',
    category: 'Other common ingredients',
    emoji: '🧲',
    whatIsIt: 'An iron-containing compound.',
    whyUsed: ['It may be used to fortify foods with iron.'],
    foundIn: ['Fortified cereals', 'Nutritional products'],
    toKnow: 'Some ingredients that sound highly technical are present because they provide essential nutrients.',
    mainFunction: 'Iron fortification',
  },
  {
    number: 21,
    name: 'Sugar',
    category: 'Sweeteners',
    emoji: '🍬',
    whatIsIt: 'Sugar is a carbohydrate used to provide sweetness.',
    whyUsed: ['Sweetness', 'Texture', 'Browning', 'Preservation in some foods'],
    foundIn: ['Almost every category of sweetened packaged food.'],
    toKnow: 'Look at the Nutrition Facts panel for Added Sugars, not just the ingredient list.',
    mainFunction: 'Sweetener',
  },
  {
    number: 22,
    name: 'High-Fructose Corn Syrup',
    category: 'Sweeteners',
    emoji: '🌽',
    whatIsIt: 'A sweetener produced from corn starch in which some glucose is converted to fructose.',
    whyUsed: ['Sweetness', 'Texture', 'Consistency', 'Food manufacturing convenience'],
    foundIn: ['Soft drinks', 'Sauces', 'Sweet baked goods', 'Processed foods'],
    toKnow: 'Like other added sugars, it contributes calories and should be considered within overall dietary intake.',
    mainFunction: 'Sweetener',
  },
  {
    number: 23,
    name: 'Dextrose',
    category: 'Sweeteners',
    emoji: '🍪',
    whatIsIt: 'Dextrose is another name for glucose.',
    whyUsed: ['Sweetness', 'Browning', 'Fermentation', 'Texture'],
    foundIn: ['Baked goods', 'Snacks', 'Processed foods'],
    mainFunction: 'Sweetener',
  },
  {
    number: 24,
    name: 'Fructose',
    category: 'Sweeteners',
    emoji: '🍐',
    whatIsIt: 'Fructose is a naturally occurring simple sugar.',
    whyUsed: ['Sweetness', 'Flavor', 'Food formulation'],
    foundIn: ['It may occur naturally in fruits or be added to processed foods.'],
    mainFunction: 'Sweetener',
  },
  {
    number: 25,
    name: 'Maltose',
    category: 'Sweeteners',
    emoji: '🌾',
    whatIsIt: 'Maltose is a sugar made from two glucose molecules.',
    whyUsed: ['Sweetness', 'Flavor', 'Fermentation'],
    foundIn: ['Malted products', 'Cereals', 'Baked goods'],
    mainFunction: 'Sweetener',
  },
  {
    number: 26,
    name: 'Aspartame',
    category: 'Sweeteners',
    emoji: '🥫',
    whatIsIt: 'Aspartame is a low-calorie sweetener that is much sweeter than sugar.',
    whyUsed: ['It provides sweetness with very little energy compared with sugar.'],
    foundIn: ['Diet beverages', 'Sugar-free products', 'Low-calorie foods'],
    toKnowLabel: 'Important',
    toKnow:
      'People with phenylketonuria (PKU) need to avoid or restrict aspartame because it contains phenylalanine.',
    mainFunction: 'High-intensity sweetener',
  },
  {
    number: 27,
    name: 'Sucralose',
    category: 'Sweeteners',
    emoji: '☕',
    whatIsIt: 'Sucralose is a high-intensity sweetener.',
    whyUsed: ['It provides sweetness with very little contribution to calories at typical use levels.'],
    foundIn: ['Low-calorie beverages', 'Sugar-free products', 'Desserts'],
    mainFunction: 'High-intensity sweetener',
  },
  {
    number: 28,
    name: 'Acesulfame Potassium',
    alsoCalled: 'Also called Ace-K.',
    category: 'Sweeteners',
    emoji: '🧴',
    whatIsIt: 'A high-intensity sweetener.',
    whyUsed: ['Provides sweetness', 'Often combined with other sweeteners'],
    foundIn: ['Beverages', 'Desserts', 'Sugar-free products'],
    mainFunction: 'High-intensity sweetener',
  },
  {
    number: 29,
    name: 'Stevia',
    category: 'Sweeteners',
    emoji: '🌱',
    whatIsIt: 'Steviol glycosides are sweet compounds derived from the leaves of the stevia plant.',
    whyUsed: ['To provide sweetness with little or no calories.'],
    foundIn: ['Beverages', 'Yogurt', 'Sweetener packets', 'Reduced-sugar products'],
    mainFunction: 'High-intensity sweetener',
  },
  {
    number: 30,
    name: 'Monk Fruit Extract',
    category: 'Sweeteners',
    emoji: '🍈',
    whatIsIt: 'Monk fruit extract contains naturally sweet compounds called mogrosides.',
    whyUsed: ['As a high-intensity sweetener.'],
    foundIn: ['Beverages', 'Baking products', 'Sugar alternatives'],
    mainFunction: 'High-intensity sweetener',
  },
  {
    number: 31,
    name: 'Red 40',
    category: 'Colors',
    emoji: '🔴',
    whatIsIt: 'Red 40 is a synthetic food color used to provide a red or reddish-orange color.',
    whyUsed: ['Adds color', 'Creates consistent appearance', 'Helps make products visually appealing'],
    foundIn: ['Candy', 'Cereals', 'Beverages', 'Desserts'],
    toKnow:
      'Food colors are regulated, but consumers may still choose to monitor them for personal or family reasons.',
    mainFunction: 'Food color',
  },
  {
    number: 32,
    name: 'Yellow 5',
    alsoCalled: 'Also known as Tartrazine.',
    category: 'Colors',
    emoji: '🟡',
    whatIsIt: 'Yellow 5 is a synthetic food color.',
    whyUsed: ['To provide yellow coloring.'],
    foundIn: ['Candy', 'Beverages', 'Cereals', 'Snacks'],
    toKnow: 'Some individuals may have sensitivities to specific food colors.',
    mainFunction: 'Food color',
  },
  {
    number: 33,
    name: 'Yellow 6',
    alsoCalled: 'Also known as Sunset Yellow FCF.',
    category: 'Colors',
    emoji: '🟠',
    whatIsIt: 'Yellow 6 is a synthetic food color.',
    whyUsed: ['To provide yellow-orange coloring.'],
    foundIn: ['Candy', 'Beverages', 'Desserts', 'Packaged snacks'],
    mainFunction: 'Food color',
  },
  {
    number: 34,
    name: 'Blue 1',
    alsoCalled: 'Also known as Brilliant Blue FCF.',
    category: 'Colors',
    emoji: '🔵',
    whatIsIt: 'Blue 1 is a synthetic food color.',
    whyUsed: ['To create blue or green colors when combined with yellow coloring.'],
    foundIn: ['Candy', 'Frosting', 'Cereals', 'Beverages'],
    mainFunction: 'Food color',
  },
  {
    number: 35,
    name: 'Caramel Color',
    category: 'Colors',
    emoji: '🟤',
    whatIsIt: 'Caramel color is produced by controlled heating of carbohydrates.',
    whyUsed: ['Adds brown color', 'Provides consistent appearance'],
    foundIn: ['Soft drinks', 'Sauces', 'Cereals', 'Baked goods'],
    toKnow: '“Caramel color” is a broad category of coloring substances, not simply the same thing as caramel candy.',
    mainFunction: 'Food color',
  },
  {
    number: 36,
    name: 'Natural Flavor',
    category: 'Flavoring ingredients',
    emoji: '🌸',
    whatIsIt: 'Natural flavor refers to flavoring substances derived from qualifying natural sources.',
    whyUsed: ['Adds flavor', 'Restores flavor lost during processing', 'Creates consistent taste'],
    foundIn: ['Beverages', 'Yogurt', 'Cereals', 'Snacks'],
    toKnow: '“Natural” describes the source of the flavoring, not whether the overall food is nutritious.',
    mainFunction: 'Flavoring',
  },
  {
    number: 37,
    name: 'Artificial Flavor',
    category: 'Flavoring ingredients',
    emoji: '⚗️',
    whatIsIt:
      'Artificial flavor refers to flavoring substances that do not qualify as natural flavor under applicable regulations.',
    whyUsed: ['Adds flavor', 'Improves consistency', 'Recreates specific flavor profiles'],
    foundIn: ['Candy', 'Beverages', 'Snacks', 'Desserts'],
    toKnow:
      'Artificial does not automatically mean unsafe. Safety depends on the specific substance, amount, and conditions of use.',
    mainFunction: 'Flavoring',
  },
  {
    number: 38,
    name: 'Monosodium Glutamate (MSG)',
    category: 'Flavoring ingredients',
    emoji: '🍜',
    whatIsIt: 'MSG is the sodium salt of glutamic acid and is used as a flavor enhancer.',
    whyUsed: ['It enhances the savory or umami character of foods.'],
    foundIn: ['Seasonings', 'Soups', 'Sauces', 'Snack foods'],
    toKnow:
      'MSG is one of the most widely discussed flavor-enhancing ingredients. Individual responses may vary, but scientific and regulatory assessments should be considered rather than relying on general claims about the ingredient.',
    mainFunction: 'Flavor enhancer',
  },
  {
    number: 39,
    name: 'Disodium Inosinate',
    category: 'Flavoring ingredients',
    emoji: '🍲',
    whatIsIt: 'A flavor enhancer often used alongside glutamates.',
    whyUsed: ['It can enhance savory flavor.'],
    foundIn: ['Snack foods', 'Seasonings', 'Instant noodles', 'Savory products'],
    mainFunction: 'Flavor enhancer',
  },
  {
    number: 40,
    name: 'Disodium Guanylate',
    category: 'Flavoring ingredients',
    emoji: '🥣',
    whatIsIt: 'Another flavor enhancer.',
    whyUsed: ['It contributes to savory flavor and is frequently used in combination with other flavor enhancers.'],
    foundIn: ['Seasonings', 'Snacks', 'Soups', 'Instant foods'],
    mainFunction: 'Flavor enhancer',
  },
  {
    number: 41,
    name: 'Sodium Citrate',
    category: 'Acidity regulators',
    emoji: '🧈',
    whatIsIt: 'A sodium salt of citric acid.',
    whyUsed: ['Controls acidity', 'Acts as a buffering agent', 'Helps manage texture in some foods'],
    foundIn: ['Cheese products', 'Beverages', 'Processed foods'],
    mainFunction: 'Acidity regulator',
  },
  {
    number: 42,
    name: 'Lactic Acid',
    category: 'Acidity regulators',
    emoji: '🥒',
    whatIsIt: 'Lactic acid is an organic acid that can be produced naturally during fermentation.',
    whyUsed: ['Controls acidity', 'Provides tartness', 'Supports food preservation'],
    foundIn: ['Fermented foods', 'Pickled products', 'Beverages', 'Sauces'],
    mainFunction: 'Acidity regulator',
  },
  {
    number: 43,
    name: 'Malic Acid',
    category: 'Acidity regulators',
    emoji: '🍎',
    whatIsIt: 'Malic acid is an organic acid naturally found in many fruits.',
    whyUsed: ['Adds tartness', 'Controls acidity', 'Enhances flavor'],
    foundIn: ['Candy', 'Beverages', 'Fruit products'],
    mainFunction: 'Acidity regulator',
  },
  {
    number: 44,
    name: 'Modified Food Starch',
    category: 'Thickeners & stabilizers',
    emoji: '🥔',
    whatIsIt:
      'Modified food starch is starch that has been physically, enzymatically, or chemically modified to give it specific properties.',
    whyUsed: ['Thickening', 'Stabilization', 'Texture', 'Moisture management'],
    foundIn: ['Sauces', 'Soups', 'Frozen foods', 'Desserts'],
    toKnow:
      '“Modified” does not mean genetically modified. The term generally refers to how the starch has been processed to change its functional properties.',
    mainFunction: 'Thickener / stabilizer',
  },
  {
    number: 45,
    name: 'Sodium Phosphate',
    category: 'Other common ingredients',
    emoji: '🍖',
    whatIsIt: 'Sodium phosphate refers to several sodium salts of phosphoric acid.',
    whyUsed: ['Texture', 'Moisture retention', 'Acidity control', 'Stabilization'],
    foundIn: ['Processed meats', 'Cheese products', 'Baked goods', 'Processed foods'],
    toKnow:
      'Depending on the specific phosphate, it may help with texture, moisture retention, acidity control, or stabilization.',
    mainFunction: 'Stabilizer / texture',
  },
  {
    number: 46,
    name: 'Silicon Dioxide',
    category: 'Other common ingredients',
    emoji: '🌶️',
    whatIsIt: 'Silicon dioxide is a mineral compound used in some foods as an anti-caking agent.',
    whyUsed: ['It can help prevent powdered ingredients from clumping.'],
    foundIn: ['Spices', 'Powdered drink mixes', 'Seasonings', 'Powdered foods'],
    toKnow: 'The name may sound unusual, but silicon dioxide has specific permitted food uses.',
    mainFunction: 'Anti-caking agent',
  },
  {
    number: 47,
    name: 'Calcium Silicate',
    category: 'Other common ingredients',
    emoji: '🧂',
    whatIsIt: 'Calcium silicate is a mineral compound.',
    whyUsed: ['Primarily as an anti-caking agent.'],
    foundIn: ['Salt', 'Spices', 'Powdered ingredients', 'Supplement powders'],
    mainFunction: 'Anti-caking agent',
  },
  {
    number: 48,
    name: 'Polysorbate 80',
    category: 'Emulsifiers',
    emoji: '🍨',
    whatIsIt: 'Polysorbate 80 is an emulsifier and stabilizer.',
    whyUsed: ['Helps ingredients stay mixed', 'Improves consistency', 'Helps stabilize food formulations'],
    foundIn: ['Ice cream', 'Sauces', 'Desserts', 'Some beverages'],
    mainFunction: 'Emulsifier / stabilizer',
  },
  {
    number: 49,
    name: 'Propylene Glycol',
    category: 'Other common ingredients',
    emoji: '🎂',
    whatIsIt:
      'Propylene glycol is a compound used in food in certain applications as a solvent, carrier, humectant, or texture-related ingredient.',
    whyUsed: ['Helps maintain moisture', 'Carries flavors or colors', 'Supports food formulation'],
    foundIn: ['Flavorings', 'Frostings', 'Processed foods'],
    toKnow: 'Its permitted use and concentration are regulated.',
    mainFunction: 'Solvent / humectant',
  },
  {
    number: 50,
    name: 'Gelatin',
    category: 'Thickeners & stabilizers',
    emoji: '🍬',
    whatIsIt: 'Gelatin is a protein obtained from collagen in animal tissues.',
    whyUsed: ['Gelling', 'Thickening', 'Stabilization', 'Texture'],
    foundIn: ['Gummies', 'Marshmallows', 'Desserts', 'Gelatin desserts'],
    toKnowLabel: 'Dietary consideration',
    toKnow:
      'Because conventional gelatin is animal-derived, it is generally not suitable for vegans. People following vegetarian diets should also check the product carefully.',
    mainFunction: 'Gelling / texture',
  },
]
